// @ts-nocheck
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Users, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FormSelect } from '@/components/shared/FormSelect';
import { FormInput } from '@/components/shared/FormInput';
import { AddMemberForm } from './AddMemberForm';
import { MemberList } from '../CrewMemberList';
import { DialogShell } from '@/components/shared/DialogShell';

import { useDialogManager } from '@/hooks/useDialogManager';
import { useCreateCrew } from '@/hooks/api/useCrews';
import { useSkills } from '@/hooks/api/useSkills';
import { useUsers } from '@/hooks/api/useUsers';
import { UserRole } from '@/types/enums';
import { CreateCrewDTO } from '@/types/crew';

export const CreateCrewForm = () => {
  const { closeDialog } = useDialogManager();
  const { createCrew, isCreating } = useCreateCrew();
  const { data: skills, isLoading: skillsLoading } = useSkills();
  const { users } = useUsers({ role: UserRole.CREW });

  const methods = useForm<CreateCrewDTO>({
    defaultValues: { name: '', skillIds: [], memberIds: [] },
  });
  const { handleSubmit, reset, watch, setValue } = methods;
  const watchedIds = watch('memberIds');

  const { availableMembers, selectedMembers } = useMemo(() => {
    const ids = new Set(watchedIds);
    return {
      availableMembers: users?.filter((u) => !ids.has(u.id)) ?? [],
      selectedMembers: users?.filter((u) => ids.has(u.id)) ?? [],
    };
  }, [users, watchedIds]);

  const onSubmit = (data: CreateCrewDTO) => {
    createCrew(data, {
      onSuccess: () => {
        toast.success(`"${data.name}" created.`);
        reset();
        closeDialog();
      },
      onError: (e) =>
        toast.error('Could not create crew.', { description: e.message }),
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0">
        <DialogShell
          icon={<Users className="w-4 h-4 text-primary" />}
          title="New Crew"
          description="Set up a new crew and optionally add members now."
          onCancel={closeDialog}
          submitText="Create Crew"
          isSubmitting={isCreating}
        >
          <fieldset className="space-y-4">
            <FormInput
              name="name"
              label="Crew Name"
              rules={{ required: 'Required' }}
            />
            <FormSelect
              name="skillIds"
              label="Skills"
              multiple
              disabled={skillsLoading}
              placeholder={skillsLoading ? 'Loading…' : 'Select skills'}
              options={
                skills?.map((s) => ({ value: s.id, label: s.name })) ?? []
              }
            />
          </fieldset>

          <div className="space-y-2.5 pt-1">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                Members
                <span className="text-xs font-normal text-muted-foreground">
                  ({selectedMembers.length})
                </span>
              </Label>
              <AddMemberForm
                members={availableMembers}
                onAddMember={(id) =>
                  setValue('memberIds', [...watchedIds, id], {
                    shouldDirty: true,
                  })
                }
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                  >
                    <UserPlus className="h-3 w-3" /> Add
                  </Button>
                }
              />
            </div>

            <MemberList
              members={selectedMembers.map((u) => ({
                id: u.id,
                name: u.name,
                email: u.email,
              }))}
              onRemove={(id) =>
                setValue(
                  'memberIds',
                  watchedIds.filter((i) => i !== id),
                  { shouldDirty: true },
                )
              }
              emptyMessage="No members added yet — optional."
            />
          </div>
        </DialogShell>
      </form>
    </FormProvider>
  );
};
